/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Route} from './config';
import {RouterStateSnapshot} from './router_state';
/**
 * \@whatItDoes Represents an event triggered when a navigation starts.
 * 
 * \@stable
 */
export class NavigationStart {
/**
 * @param {?} id
 * @param {?} url
 */
constructor(
public id: number,
public url: string) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string { return `NavigationStart(id: ${this.id}, url: '${this.url}')`; }
}

function NavigationStart_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationStart.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationStart.prototype.url;
}

/**
 * \@whatItDoes Represents an event triggered when a navigation ends successfully.
 * 
 * \@stable
 */
export class NavigationEnd {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} urlAfterRedirects
 */
constructor(
public id: number,
public url: string,
public urlAfterRedirects: string) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string {
    return `NavigationEnd(id: ${this.id}, url: '${this.url}', urlAfterRedirects: '${this.urlAfterRedirects}')`;
  }
}

function NavigationEnd_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationEnd.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationEnd.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationEnd.prototype.urlAfterRedirects;
}

/**
 * \@whatItDoes Represents an event triggered when a navigation is canceled.
 * 
 * \@stable
 */
export class NavigationCancel {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} reason
 */
constructor(
public id: number,
public url: string,
public reason: string) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string { return `NavigationCancel(id: ${this.id}, url: '${this.url}')`; }
}

function NavigationCancel_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationCancel.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationCancel.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationCancel.prototype.reason;
}

/**
 * \@whatItDoes Represents an event triggered when a navigation fails due to an unexpected error.
 * 
 * \@stable
 */
export class NavigationError {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} error
 */
constructor(
public id: number,
public url: string,
public error: any) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string {
    return `NavigationError(id: ${this.id}, url: '${this.url}', error: ${this.error})`;
  }
}

function NavigationError_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationError.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationError.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
NavigationError.prototype.error;
}

/**
 * \@whatItDoes Represents an event triggered when routes are recognized.
 * 
 * \@stable
 */
export class RoutesRecognized {
/**
 * @param {?} id
 * @param {?} url
 * @param {?} urlAfterRedirects
 * @param {?} state
 */
constructor(
public id: number,
public url: string,
public urlAfterRedirects: string,
public state: RouterStateSnapshot) {}
/**
 * \@docsNotRequired
 * @return {?}
 */
toString(): string {
    return `RoutesRecognized(id: ${this.id}, url: '${this.url}', urlAfterRedirects: '${this.urlAfterRedirects}', state: ${this.state})`;
  }
}

function RoutesRecognized_tsickle_Closure_declarations() {
/**
 * \@docsNotRequired
 * @type {?}
 */
RoutesRecognized.prototype.id;
/**
 * \@docsNotRequired
 * @type {?}
 */
RoutesRecognized.prototype.url;
/**
 * \@docsNotRequired
 * @type {?}
 */
RoutesRecognized.prototype.urlAfterRedirects;
/**
 * \@docsNotRequired
 * @type {?}
 */
RoutesRecognized.prototype.state;
}

/**
 * \@whatItDoes Represents an event triggered before lazy loading a route config.
 * 
 * \@experimental
 */
export class RouteConfigLoadStart {
/**
 * @param {?} route
 */
constructor(public route: Route) {}
/**
 * @return {?}
 */
toString(): string { return `RouteConfigLoadStart(path: ${this.route.path})`; }
}

function RouteConfigLoadStart_tsickle_Closure_declarations() {
/** @type {?} */
RouteConfigLoadStart.prototype.route;
}

/**
 * \@whatItDoes Represents an event triggered when a route has been lazy loaded.
 * 
 * \@experimental
 */
export class RouteConfigLoadEnd {
/**
 * @param {?} route
 */
constructor(public route: Route) {}
/**
 * @return {?}
 */
toString(): string { return `RouteConfigLoadEnd(path: ${this.route.path})`; }
}

function RouteConfigLoadEnd_tsickle_Closure_declarations() {
/** @type {?} */
RouteConfigLoadEnd.prototype.route;
}


/**
 * @whatItDoes Represents a router event.
 *
 * One of:
 * - {@link NavigationStart},
 * - {@link NavigationEnd},
 * - {@link NavigationCancel},
 * - {@link NavigationError},
 * - {@link RoutesRecognized},
 * - {@link RouteConfigLoadStart},
 * - {@link RouteConfigLoadEnd}
 *
 * @stable
 */
export type Event = NavigationStart | NavigationEnd | NavigationCancel | NavigationError |
    RoutesRecognized | RouteConfigLoadStart | RouteConfigLoadEnd;
